import { Chart } from "chart.js";
import { makeAutoObservable, reaction, runInAction } from "mobx";
import agent from "../api/agent";
import { RkdSelectOptions } from "../component/form/RkdSelect";
import { IDashboardTrendChart } from "../models/dashboard";
import { format } from 'date-fns'
import { store } from "./store";

interface HomeTrendChartDate {
    month: number;
    year: number;
}

export interface IGenerateDashboardParams {
    stationId: number
    type: number;
    startDate: Date
    endDate: Date
}

export class HomeTrendChartStore {
    date: HomeTrendChartDate = {
        month: new Date().getMonth(),
        year: new Date().getFullYear()
    }
    typeOptions: RkdSelectOptions[] = [
        {
            text: 'Reliability',
            value: 0,
        },
        {
            text: 'Availability',
            value: 1,
        },
        {
            text: 'Maintainability',
            value: 2,
        },
    ]
    errorMessage: string | null = null
    selectedtype: number = 0
    selectedStartDate: Date = new Date(this.date.year - 1, this.date.month, 2)
    selectedEndDate: Date = new Date(this.date.year, this.date.month, 2)
    
    chart: Chart<any, any, any> | null = null
    chartData: IDashboardTrendChart | null = null;
    chartStation: Chart<any, any, any> | null = null
    chartDataStation: IDashboardTrendChart | null = null;
    loadingChart = false;

    constructor() {
        makeAutoObservable(this)
        
        // validasi startDate dan endDate
        reaction(() => [this.selectedStartDate, this.selectedEndDate, this.selectedtype], 
            () => {
                const startDate = this.selectedStartDate as any
                const endDate = this.selectedEndDate as any

                const time = Math.abs(endDate - startDate);
                const days = Math.ceil(time / (1000 * 60 * 60 * 24)); 
                
                if (!startDate || !endDate) {
                    this.setErrorMessage('Start Date or End Date is required fields')
                } else if (
                    (startDate !instanceof Date && isNaN(startDate as any)) 
                    || (endDate !instanceof Date && isNaN(endDate as any))
                ) {
                    this.setErrorMessage('Please input a valid Date')
                } else if (Date.parse(endDate) <= Date.parse(startDate)) {
                    this.setErrorMessage('End Date must be greater than Start Date')
                } else if (Date.parse(endDate) === Date.parse(startDate)) {
                    this.clearErrorMessage()
                } else if (days > 365) {
                    this.setErrorMessage('Date Range must be within one year')
                } else {
                    this.clearErrorMessage()
                    if (store.commonStore.selectedStation) {
                        this.generateChart(store.commonStore.selectedStation.id)
                    }
                }
            }
        )
    }

    generateChart = async (stationId: number) => {
        this.loadingChart = true;
        try {
            const data = {
                stationId: stationId,
                type: this.selectedtype,
                endDate: this.selectedEndDate,
                startDate: this.selectedStartDate,
            }
            const res = await agent.Dashboard.generateChart(data)
            const resStation = await agent.Dashboard.generateChartStation(data)

            runInAction(() => this.chartData = res)
            runInAction(() => this.chartDataStation = resStation)
        } catch (error) {
            throw error
        } finally {
            runInAction(() => this.loadingChart = false)
        }
    }

    setErrorMessage = (message: string) => {
        this.errorMessage = message
    }
    clearErrorMessage = () => {
        this.errorMessage = null
    }

    setSelectedType = (value: number) => {
        this.selectedtype = value
    }
    setSelectedStartDate = (value: Date) => {
        this.selectedStartDate = value
    }
    setSelectedEndDate = (value: Date) => {
        this.selectedEndDate = value
    }
    
    downloadChart = (stationName: string) => {
        // setup filename
        const currentSelectedType = this.typeOptions.find((i) => i.value === this.selectedtype)
        const now = format(new Date(), 'yyyyMMdd')
        const start = format(this.selectedStartDate, 'yyyyMM');
        const end = format(this.selectedEndDate, 'yyyyMM');
        const name = stationName;
        const type = currentSelectedType?.text
        const filename = `${now}_Trend Chart_${name}_${type}_${start}-${end}`;
        
        // get canvas
        const stationCanvas = window.document.getElementById('line-chart-station') as HTMLCanvasElement;
        const systemCanvas = window.document.getElementById('line-chart-system') as HTMLCanvasElement;

        if (!stationCanvas || !systemCanvas) 
            return store.snackbarStore.show('info', 'Wait until chart is generated.')

        // setup canvas
        const canvas = document.createElement('canvas')
        const canvasCtx = canvas.getContext('2d');
        if (canvasCtx) {
            canvasCtx.beginPath();
            canvasCtx.fillStyle = 'red';
            canvasCtx.rect(0, 0, 200, 200);
            canvasCtx.fill();
            (canvasCtx?.canvas as HTMLCanvasElement).height = 1200;
            (canvasCtx?.canvas as HTMLCanvasElement).width = 1750;
            canvasCtx?.drawImage(stationCanvas, 0, 0)
            canvasCtx?.drawImage(systemCanvas, 0, 600)
        }

        const url = canvas?.toDataURL('image/png', 'image/octet-stream')

        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.click()
        a.remove()
    }
    setChart = (chart: any, type: 'station' | 'system') => {
        if (type === 'station') {
            this.chartStation = chart
        } else {
            this.chart = chart
        }
    }
}
